import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import api from "@/lib/api";
import {
  HeartPulse, LayoutDashboard, Mic, FileText, Users, Pill, UserCheck, LogOut, Bell,
  CalendarPlus, CalendarDays, CalendarClock, Globe, Loader2, Sparkles, HelpCircle, PlayCircle, Video,
} from "lucide-react";
import Tour, { doctorTourSteps, assistantTourSteps } from "@/components/Tour";
import DemoPlayer from "@/components/DemoPlayer";
import HomeDashboard from "@/components/HomeDashboard";
import LiveConsultationAI from "@/components/LiveConsultationAI";
import PrescriptionPad from "@/components/PrescriptionPad";
import PatientRegistry from "@/components/PatientRegistry";
import MedicineLabDirectory from "@/components/MedicineLabDirectory";
import DoctorProfile from "@/components/DoctorProfile";
import BookAppointment from "@/components/BookAppointment";
import AppointmentsList from "@/components/AppointmentsList";
import AskMe from "@/components/AskMe";
import DoctorTeleconsults from "@/components/DoctorTeleconsults";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import Footer from "@/components/Footer";

const TABS = [
  { id: "home", label: "Home", key: "home", icon: LayoutDashboard },
  { id: "consultation", label: "Live Consult & AI Rx", key: "live_consult", icon: Mic },
  { id: "askme", label: "Ask Me", key: "ask_me", icon: Sparkles },
  { id: "book", label: "Book Appointment", key: "book_appointment", icon: CalendarPlus },
  { id: "appointments", label: "Booked Appointments", key: "booked_appointments", icon: CalendarDays },
  { id: "teleconsults", label: "Teleconsults", key: "teleconsults", icon: Video },
  { id: "prescriptions", label: "Prescription Pad", key: "prescription_pad", icon: FileText },
  { id: "patients", label: "Patient Data", key: "patient_data", icon: Users },
  { id: "followups", label: "Upcoming Follow-ups", key: "upcoming_followups", icon: CalendarClock },
  { id: "profile", label: "Profile", key: "profile", icon: UserCheck },
];

export default function Dashboard() {
  const { user, logout, refresh } = useAuth();
  const { t } = useI18n();
  const isAssistant = user?.role === "assistant";
  const [active, setActive] = useState(isAssistant ? "book" : "home");
  const [dueCount, setDueCount] = useState(0);
  const [gotoPatient, setGotoPatient] = useState(null);
  const [tourRun, setTourRun] = useState(false);
  const [showDemo, setShowDemo] = useState(false);
  const [tourSeen, setTourSeen] = useState(false);

  const visibleTabs = isAssistant
    ? TABS.filter((tab) => ["book", "appointments", "profile"].includes(tab.id))
    : TABS;

  useEffect(() => {
    if (isAssistant) return;
    api.get("/home/summary").then((r) => setDueCount(r.data.stats.due_today)).catch(() => {});
  }, [active]);

  // First-time interactive walkthrough (auto-shows once per account)
  useEffect(() => {
    if (!user || tourSeen || user.tour_done) return;
    const timer = setTimeout(() => { setTourRun(true); setTourSeen(true); }, 700);
    return () => clearTimeout(timer);
  }, [user, tourSeen]);

  const startTour = () => setTourRun(true);
  const finishTour = async () => {
    setTourRun(false);
    try { await api.put("/profile", { tour_done: true }); await refresh(); } catch {}
  };

  const openConsultForPatient = (pid) => {
    setGotoPatient(pid);
    setActive("consultation");
  };

  const render = (id) => {
    switch (id) {
      case "home": return <HomeDashboard active={active === "home"} onOpenConsult={openConsultForPatient} onGoTab={setActive} onWatchDemo={() => setShowDemo(true)} />;
      case "consultation": return <LiveConsultationAI presetPatientId={gotoPatient} clearPreset={() => setGotoPatient(null)} />;
      case "askme": return <AskMe />;
      case "book": return <BookAppointment />;
      case "appointments": return <AppointmentsList onOpenConsult={openConsultForPatient} />;
      case "teleconsults": return <DoctorTeleconsults />;
      case "prescriptions": return <PrescriptionPad />;
      case "patients": return <PatientRegistry mode="data" onOpenConsult={openConsultForPatient} />;
      case "followups": return <PatientRegistry mode="followups" onOpenConsult={openConsultForPatient} />;
      case "inventory": return <MedicineLabDirectory />;
      case "profile": return <DoctorProfile />;
      default: return null;
    }
  };

  const profileIncomplete = !isAssistant && (!user?.registration_number || !user?.specialty);

  return (
    <div className="min-h-screen bg-[#FBFBF7]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-amber-100/70 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-2xl bg-[#1E5AA8] flex items-center justify-center shadow-md shrink-0">
              <HeartPulse className="w-6 h-6 text-amber-300" />
            </div>
            <div className="min-w-0">
              <h1 className="font-display text-lg font-bold text-[#123C73] leading-none truncate">
                {user?.clinic_name || "MDvia Care"}
              </h1>
              <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                {user?.full_name || user?.name}
                {user?.qualifications ? `, ${user.qualifications}` : ""}
                {user?.registration_number ? ` · Reg: ${user.registration_number}` : ""}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 shrink-0">
            <LanguageSwitcher />
            <button data-testid="header-watch-demo" onClick={() => setShowDemo(true)}
              className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-2 rounded-xl hover:bg-blue-50 text-slate-600 text-sm" title="Watch demo">
              <PlayCircle className="w-5 h-5" />
            </button>
            <button data-testid="replay-tour-btn" onClick={startTour}
              className="p-2 rounded-xl hover:bg-amber-50 text-slate-500 transition-colors" title="Take a tour">
              <HelpCircle className="w-5 h-5" />
            </button>
            {!isAssistant && (
              <button data-testid="header-followup-bell" onClick={() => setActive("followups")}
                className="relative p-2 rounded-xl hover:bg-amber-50 transition-colors">
                <Bell className="w-5 h-5 text-slate-500" />
                {dueCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#F59E0B] text-white text-[10px] font-bold flex items-center justify-center">
                    {dueCount}
                  </span>
                )}
              </button>
            )}
            <div className="flex items-center gap-2 pl-2 sm:pl-3 border-l border-slate-200">
              {user?.picture ? (
                <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-slate-200" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-[#EBF3FB] text-[#1E5AA8] flex items-center justify-center font-bold text-sm">
                  {(user?.name || "D")[0]}
                </div>
              )}
              <button data-testid="google-signout-btn" onClick={logout}
                className="p-2 rounded-xl hover:bg-red-50 text-slate-500 hover:text-red-600 transition-colors" title="Sign out">
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-[1400px] mx-auto px-2 sm:px-6 relative">
          <div data-testid="tab-scroll" className="flex gap-1 overflow-x-auto tab-scrollbar scroll-smooth">
            {visibleTabs.map((tab) => {
              const on = active === tab.id;
              return (
                <button
                  key={tab.id}
                  data-testid={`nav-tab-${tab.id}`}
                  onClick={() => setActive(tab.id)}
                  className={`relative flex items-center gap-2 px-3.5 sm:px-4 py-3 text-sm whitespace-nowrap transition-colors ${
                    on ? "text-[#1E5AA8] font-semibold" : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {t(tab.key)}
                  {tab.id === "profile" && profileIncomplete && (
                    <span className="w-1.5 h-1.5 rounded-full bg-[#F59E0B]" />
                  )}
                  {on && (
                    <motion.div layoutId="tab-underline" className="absolute left-2 right-2 -bottom-px h-0.5 rounded-full bg-[#1E5AA8]" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        {visibleTabs.map((tab) => (
          <div key={tab.id} style={{ display: active === tab.id ? "block" : "none" }} className={active === tab.id ? "tab-fade" : ""}>
            {render(tab.id)}
          </div>
        ))}
      </main>
      <Footer />

      {tourRun && (
        <Tour steps={isAssistant ? assistantTourSteps() : doctorTourSteps()} onClose={finishTour} />
      )}
      <DemoPlayer open={showDemo} onClose={() => setShowDemo(false)} />
    </div>
  );
}
